/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Loader2 } from 'lucide-react';

import type { JSX, ReactNode } from 'react';

interface QueryStateProps {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly isEmpty: boolean;
  readonly loadingLabel: string;
  readonly errorLabel: string;
  readonly emptyLabel: string;
  readonly children: ReactNode;
}

function Centered({ children }: { readonly children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-slate-500">
      {children}
    </div>
  );
}

/**
 * Renders the standard loading / error / empty states for a list query, or the
 * children when data is present. Keeps every list page free of this boilerplate.
 */
export function QueryState({
  isLoading,
  isError,
  isEmpty,
  loadingLabel,
  errorLabel,
  emptyLabel,
  children,
}: QueryStateProps): ReactNode {
  if (isLoading) {
    return (
      <Centered>
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" aria-hidden="true" />
        <span>{loadingLabel}</span>
      </Centered>
    );
  }
  if (isError) {
    return (
      <Centered>
        <p role="alert" className="text-red-600">
          {errorLabel}
        </p>
      </Centered>
    );
  }
  if (isEmpty) {
    return <Centered>{emptyLabel}</Centered>;
  }
  return children;
}
